<template>
	<div class="product-service-page">
		<!-- Hero Banner -->
		<div class="hero-banner">
			<div class="hero-container">
				<div class="hero-content">
					<h1>{{ $t('hero.title') }}</h1>
					<p>{{ $t('hero.subtitle') }}</p>
				</div>
			</div>
		</div>

		<!-- Nav Arrows -->
		<!-- <div class="nav-arrows">
			<div class="nav-arrow" @click="prevService" title="上一个服务"><i class="fas fa-chevron-up"></i></div>
			<div class="nav-arrow" @click="nextService" title="下一个服务"><i class="fas fa-chevron-down"></i></div>
		</div> -->

		<!-- Main Content: Sidebar + Panels -->
		<div class="services-main">
			<!-- Sidebar -->
			<aside class="sidebar">
				<div class="sidebar-title">{{ $t('services') }}</div>
				<ul class="service-list">
					<li
						v-for="(svc, index) in sidebarServices"
						:key="index"
						:class="{ active: currentService === index }"
						@click="switchService(index)"
					>
						<span class="svc-icon"><i :class="svc.icon"></i></span>
						{{ $t(svc.i18nKey) }}
						<span class="dot"></span>
					</li>
				</ul>

				<!-- Emergency Card -->
				<div class="emergency-card">
					<div class="card-icon"><i class="fas fa-headset"></i></div>
					<h3>{{ $t('servicesPage.emergency.title') }}</h3>
					<p>{{ $t('servicesPage.emergency.description') }}</p>
					<div class="phone"><i class="fas fa-phone"></i> 13401038003</div>
				</div>

				<!-- CTA -->
				<div class="sidebar-cta">
					<h4>{{ $t('servicesPage.cta.title') }}</h4>
					<p>{{ $t('servicesPage.cta.description') }}</p>
					<router-link to="/contact" class="cta-btn">{{ $t('contactUs') }}</router-link>
				</div>
			</aside>

			<!-- Content Area -->
			<div class="content-area" ref="contentArea" @scroll="handleScroll">
				<div class="service-panel" :class="{ active: currentService === 0 }">
					<Shipping />
				</div>
				<div class="service-panel" :class="{ active: currentService === 1 }">
					<RefrigeratedGoods />
				</div>
				<div class="service-panel" :class="{ active: currentService === 2 }">
					<OperableGoods />
				</div>
				<div class="service-panel" :class="{ active: currentService === 3 }">
					<Consultation />
				</div>
			
				<!-- 滚动到底部提示 -->
				<transition name="hint-slide">
					<div v-if="showScrollHint" class="scroll-hint">
						<div class="hint-content">
							<span class="hint-text">{{ $t('servicesPage.scrollHint.text') }}</span>
							<div class="hint-arrows">
								<i class="fas fa-chevron-up"></i>
								<i class="fas fa-chevron-up"></i>
							</div>
						</div>
					</div>
				</transition>
			</div>
		</div>
	</div>
</template>

<script>
	import Shipping from './Shipping.vue';
	import RefrigeratedGoods from './RefrigeratedGoods.vue';
	import OperableGoods from './OperableGoods.vue';
	import Consultation from './Consultation.vue';

	export default {
		name: 'Services',
		components: {
			Shipping,
			RefrigeratedGoods,
			OperableGoods,
			Consultation,
		},
		data() {
			return {
				currentService: 0,
				sidebarServices: [
					{ i18nKey: 'shipping', icon: 'fas fa-plane' },
					{ i18nKey: 'refrigerated', icon: 'fas fa-snowflake' },
					{ i18nKey: 'operable', icon: 'fas fa-boxes-stacked' },
					{ i18nKey: 'consultation', icon: 'fas fa-file-shield' },
				],
				showScrollHint: false,
				scrollTimer: null,
			};
		},
		mounted() {
			this.$nextTick(() => {
				this.updateContentAreaHeight();
				setTimeout(() => this.updateContentAreaHeight(), 200);
				setTimeout(() => this.updateContentAreaHeight(), 600);
			});
			window.addEventListener('resize', this.updateContentAreaHeight);
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.updateContentAreaHeight);
			if (this.scrollTimer) {
				clearTimeout(this.scrollTimer);
			}
		},
		created() {
			this.syncServiceFromRoute();
		},
		watch: {
			currentService() {
				// 切换服务后，等子组件渲染完毕滚动到顶部
				this.$nextTick(() => {
					const el = this.$refs.contentArea;
					if (el) el.scrollTop = 0;
				});
				setTimeout(() => {
					const el = this.$refs.contentArea;
					if (el) el.scrollTop = 0;
				}, 100);
				// 同步 URL
				this.updateRouteFromService();
			},
			'$route.path'() {
				this.syncServiceFromRoute();
			},
		},
		methods: {
			switchService(index) {
				this.currentService = index;
				this.resetScroll();
				this.cancelScrollHint();
			},
			prevService() {
				this.currentService = (this.currentService - 1 + 4) % 4;
				this.resetScroll();
				this.cancelScrollHint();
			},
			nextService() {
				this.currentService = (this.currentService + 1) % 4;
				this.resetScroll();
				this.cancelScrollHint();
			},
			resetScroll() {
				const el = this.$refs.contentArea;
				if (el) el.scrollTop = 0;
			},
			cancelScrollHint() {
				this.showScrollHint = false;
				if (this.scrollTimer) {
					clearTimeout(this.scrollTimer);
					this.scrollTimer = null;
				}
			},
			updateContentAreaHeight() {
				const el = this.$refs.contentArea;
				if (!el) return;
				if (window.innerWidth <= 1024) {
					el.style.maxHeight = '';
					el.style.overflowY = '';
					return;
				}
				const contentRect = el.getBoundingClientRect();
				const mainEl = el.closest('.services-main');
				let availableHeight;
				if (mainEl) {
					const mainRect = mainEl.getBoundingClientRect();
					availableHeight = mainRect.bottom - contentRect.top;
				} else {
					availableHeight = window.innerHeight - contentRect.top;
				}
				el.style.maxHeight = Math.max(200, availableHeight) + 'px';
				el.style.overflowY = 'auto';
			},
			handleScroll() {
				const el = this.$refs.contentArea;
				if (!el) return;
				if (window.innerWidth <= 1024 || el.style.overflowY !== 'auto') return;
				const hasScroll = el.scrollHeight > el.clientHeight + 40;
				if (!hasScroll) return;
				const isBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
				if (isBottom) {
					if (!this.showScrollHint && !this.scrollTimer) {
						this.showScrollHint = true;
						this.scrollTimer = setTimeout(() => {
							this.showScrollHint = false;
							this.scrollTimer = null;
							this.nextService();
						}, 1200);
					}
				} else {
					if (this.showScrollHint) {
						this.cancelScrollHint();
					}
				}
			},
			syncServiceFromRoute() {
				const path = this.$route.path;
				let target;
				if (path.includes('/shipping')) target = 0;
				else if (path.includes('/refrigerated')) target = 1;
				else if (path.includes('/operable')) target = 2;
				else if (path.includes('/consultation')) target = 3;
				else target = 0;
				if (target !== this.currentService) {
					this.currentService = target;
				}
			},
			updateRouteFromService() {
				const routes = ['/services/shipping', '/services/refrigerated', '/services/operable', '/services/consultation'];
				const target = routes[this.currentService];
				if (target && this.$route.path !== target) {
					this.$router.replace(target).catch(() => {});
				}
			},
		},
	};
</script>

<style lang="scss" scoped>
	// === Color Scheme (aligned with Home.vue) ===
	$primary: #0052CC;
	$primary-dark: #0043A8;
	$primary-light: #3B7FFF;
	$dark: #0A1628;
	$heading: #0A1628;
	$body: #5B6C7F;
	$light-bg: #F4F7FB;

	.product-service-page {
		font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
		color: $body;
		line-height: 1.6;
		background: $light-bg;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}

	/* ====== Hero Banner (aligned with About.vue) ====== */
	.hero-banner {
		width: 100%;
		height: 60vh;
		min-height: 400px;
		position: relative;
		border-radius: 20px;
		overflow: hidden;
		background: linear-gradient(135deg, rgba(10, 22, 40, .75) 0%, rgba(0, 82, 204, .3) 100%),
			url('../assets/images/zjwm.png') center/cover no-repeat;
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 4px 20px rgba(0, 60, 120, 0.1);

		.hero-container {
			max-width: 1200px;
			width: 90%;
			margin: 0 auto;
			padding: 40px;
			background-color: rgba(255, 255, 255, 0.2);
			backdrop-filter: blur(8px);
			border-radius: 12px;
			border: 1px solid rgba(255, 255, 255, 0.3);
			box-shadow: 0 8px 32px rgba(0, 60, 120, 0.1);
		}

		.hero-content {
			max-width: 800px;
			text-align: center;
			animation: fadeInUp 1.2s ease-out;

			h1 {
				font-size: 2.6rem;
				font-weight: 800;
				margin-bottom: 1.5rem;
				color: #fff;
				text-shadow: 0 2px 12px rgba(0, 60, 120, 0.6);
				letter-spacing: -0.5px;
				line-height: 1.2;
				position: relative;
				display: inline-block;

				&::after {
					content: '';
					display: block;
					width: 80px;
					height: 4px;
					background: rgba(255, 255, 255, 0.7);
					margin: 20px auto;
					border-radius: 2px;
				}
			}

			p {
				font-size: 1.2rem;
				color: rgba(255, 255, 255, 0.9);
				margin: 0;
			}
		}

		@media (max-width: 1200px) {
			height: 60vh;
			min-height: 500px;
		}

		@media (max-width: 1100px) {
			height: 55vh;
			min-height: 450px;
		}

		@media (max-width: 768px) {
			height: 50vh;
			min-height: 400px;

			.hero-container {
				padding: 0 20px;
			}

			.hero-content {
				h1 { font-size: 2rem; }
				p { font-size: 1rem; }
			}
		}

		@media (max-width: 480px) {
			height: 45vh;
			min-height: 350px;

			.hero-content h1 { font-size: 1.8rem; }
		}
	}

	/* ====== Nav Arrows ====== */
	.nav-arrows {
		position: fixed;
		right: 35px;
		top: 50%;
		transform: translateY(-50%);
		display: flex;
		flex-direction: column;
		gap: 12px;
		z-index: 100;
	}

	.nav-arrow {
		width: 46px;
		height: 46px;
		border-radius: 50%;
		background: rgba(0, 48, 87, 0.85);
		color: #fff;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		font-size: 1rem;
		transition: all 0.3s;
		border: 2px solid rgba(255, 255, 255, 0.2);
		backdrop-filter: blur(8px);
		box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

		&:hover {
			background: $primary;
			border-color: $primary;
			transform: scale(1.1);
		}

		&:active {
			transform: scale(0.95);
		}
	}

	/* ====== Main Layout ====== */
	.services-main {
		max-width: 1400px;
		margin: 0 auto;
		padding: 50px 30px 80px;
		display: flex;
		gap: 40px;
		position: relative;
	}

	/* ====== Sidebar ====== */
	.sidebar {
		width: 300px;
		flex-shrink: 0;
		position: sticky;
		top: 100px;
		align-self: flex-start;
	}

	.sidebar-title {
		font-size: 1.35rem;
		font-weight: 700;
		color: $dark;
		margin-bottom: 20px;
		padding-bottom: 12px;
		border-bottom: 3px solid $primary;
		display: inline-block;
	}

	.service-list {
		list-style: none;
		margin-bottom: 28px;
		padding: 0;

		li {
			position: relative;
			padding: 15px 20px;
			margin-bottom: 4px;
			background: #f8f8f5;
			border-radius: 8px;
			cursor: pointer;
			font-size: 1.02rem;
			font-weight: 500;
			color: #444;
			transition: all 0.3s ease;
			display: flex;
			align-items: center;
			gap: 14px;

			&:hover {
				background: #f0ece4;
				transform: translateX(4px);
			}

			&.active {
				background: $primary;
				color: #fff;
				font-weight: 600;
				box-shadow: 0 4px 15px rgba(0, 82, 204, 0.35);

				.svc-icon {
					color: #fff;
				}

				.dot {
					opacity: 1;
				}
			}

			.svc-icon {
				width: 22px;
				text-align: center;
				color: $primary;
				font-size: 1.05rem;
				transition: color 0.3s;
			}

			.dot {
				position: absolute;
				right: 18px;
				width: 8px;
				height: 8px;
				border-radius: 50%;
				background: #fff;
				opacity: 0;
				transition: opacity 0.3s;
			}
		}
	}

	.emergency-card {
		background: linear-gradient(135deg, $dark, lighten($dark, 5%));
		border-radius: 14px;
		padding: 26px 22px;
		color: #fff;
		margin-bottom: 22px;

		.card-icon {
			width: 42px;
			height: 42px;
			border-radius: 50%;
			background: rgba(255, 255, 255, 0.15);
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 14px;
			font-size: 1.1rem;
		}

		h3 {
			font-size: 1.15rem;
			margin-bottom: 8px;
		}

		p {
			font-size: 0.88rem;
			opacity: 0.85;
			margin-bottom: 12px;
			line-height: 1.5;
		}

		.phone {
			font-size: 1.25rem;
			font-weight: 700;

			i {
				margin-right: 8px;
				font-size: 0.95rem;
			}
		}
	}

	.sidebar-cta {
		background: #fafaf7;
		border-radius: 14px;
		padding: 26px 22px;
		border: 1px solid #e8e4dc;

		h4 {
			font-size: 1.02rem;
			color: $dark;
			margin-bottom: 10px;
		}

		p {
			font-size: 0.86rem;
			color: #777;
			margin-bottom: 16px;
			line-height: 1.6;
		}
	}

	.cta-btn {
		display: inline-block;
		background: $primary;
		color: #fff;
		padding: 11px 30px;
		border-radius: 8px;
		text-decoration: none;
		font-weight: 600;
		font-size: 0.92rem;
		transition: all 0.3s;
		border: none;
		cursor: pointer;

		&:hover {
			background: $primary-dark;
			transform: translateY(-2px);
			box-shadow: 0 4px 12px rgba(0, 82, 204, 0.3);
		}
	}

	/* ====== Content Area ====== */
	.content-area {
		flex: 1;
		min-width: 0;
		position: relative;
		/* 隐藏滚动条 */
		&::-webkit-scrollbar { display: none; }
		-ms-overflow-style: none;
		scrollbar-width: none;
	}

	.scroll-hint {
		padding: 32px 20px 24px;
		text-align: center;
		background: linear-gradient(to top, rgba(244, 247, 251, 1) 0%, rgba(244, 247, 251, 0) 100%);
		pointer-events: none;

		.hint-content {
			display: inline-flex;
			flex-direction: column;
			align-items: center;
			gap: 10px;
			color: $primary;
			font-weight: 600;
			font-size: 0.95rem;
		}

		.hint-text {
			letter-spacing: 1px;
		}

		.hint-arrows {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 2px;

			i {
				font-size: 1.1rem;
				animation: bounceUp 0.8s infinite ease-in-out;

				&:nth-child(2) {
					animation-delay: 0.15s;
					opacity: 0.7;
				}
			}
		}
	}

	@keyframes bounceUp {
		0%, 100% {
			transform: translateY(0);
			opacity: 0.4;
		}
		50% {
			transform: translateY(-8px);
			opacity: 1;
		}
	}

	.hint-slide-enter-active,
	.hint-slide-leave-active {
		transition: all 0.4s ease;
	}

	.hint-slide-enter,
	.hint-slide-leave-to {
		opacity: 0;
		transform: translateY(20px);
	}

	.service-panel {
		display: none;
		animation: fadeInUp 0.45s ease;

		&.active {
			display: block;
		}
	}

	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(18px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	/* ====== Bottom Banner ====== */
	.bottom-banner {
		background: linear-gradient(135deg, $primary, $primary-light);
		padding: 55px 40px;
		text-align: center;
		color: #fff;
		position: relative;
		overflow: hidden;

		&::before {
			content: '';
			position: absolute;
			top: -60px;
			left: -60px;
			width: 200px;
			height: 200px;
			border-radius: 50%;
			background: rgba(255, 255, 255, 0.08);
		}

		h3 {
			font-size: 1.9rem;
			margin-bottom: 12px;
			position: relative;
			z-index: 1;
		}

		p {
			font-size: 1.05rem;
			opacity: 0.9;
			margin-bottom: 24px;
			position: relative;
			z-index: 1;
		}
	}

	.banner-btn {
		display: inline-block;
		background: $primary-dark;
		color: #fff;
		padding: 13px 38px;
		border-radius: 10px;
		font-weight: 600;
		font-size: 0.95rem;
		text-decoration: none;
		transition: all 0.3s;
		position: relative;
		z-index: 1;

		&:hover {
			background: darken($primary-dark, 8%);
			transform: translateY(-2px);
			box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
		}
	}

	/* ====== Responsive ====== */
	@media (max-width: 1024px) {
		.services-main {
			flex-direction: column;
			padding: 30px 20px 60px;
		}

		.sidebar {
			width: 100%;
			position: static;
		}

		.service-list {
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			gap: 8px;
		}

		.nav-arrows {
			right: 12px;
		}

		.nav-arrow {
			width: 38px;
			height: 38px;
			font-size: 0.85rem;
		}
	}

	@media (max-width: 600px) {
		.service-list {
			grid-template-columns: 1fr;
		}

		h1 {
			font-size: 1.7rem;
		}
	}
</style>